/**
 * Naked & Famous Denim NYC storefront customizations that need to survive
 * Shopify theme upgrades.
 */

const WORLDWIDE_LINK_SELECTOR = '#header-component a';
const MAIN_SITE_BRIDGE_SELECTOR = '.nyc-main-bridge[data-handle]';

/**
 * Styles Worldwide menu links as utility links and opens them in a new tab.
 * The header can be re-rendered by Shopify, so this is safe to run repeatedly.
 *
 * @param {ParentNode} [root]
 */
function enhanceWorldwideLinks(root = document) {
  for (const link of root.querySelectorAll(WORLDWIDE_LINK_SELECTOR)) {
    if (!link.textContent?.toLowerCase().includes('worldwide')) continue;

    const title = link.querySelector('.menu-list__link-title');
    title?.classList.add('nfd-worldwide-title');
    link.setAttribute('target', '_blank');
    link.setAttribute('rel', 'noopener');
  }
}

/**
 * Moves a confirmed main-site availability link into the sold-out badge for
 * the same product card. This keeps both stock messages together and makes
 * the destination unambiguous.
 *
 * @param {HTMLElement} bridge
 */
function integrateMainSiteBridge(bridge) {
  if (bridge.dataset.nfdIntegrated === 'true' || bridge.style.display === 'none') return;

  const card = bridge.closest('product-card');
  const soldOutBadge = card?.querySelector('.product-badges__badge.color-custom-badge-sold-out');
  const bridgeLink = bridge.querySelector('a[href]');

  if (!card || !soldOutBadge || !bridgeLink) return;

  const productTitle = card.querySelector('.product-card__link')?.getAttribute('aria-label');
  const integratedBadge = document.createElement('a');
  integratedBadge.className =
    'product-badges__badge product-badges__badge--rectangle nfd-main-site-badge';
  integratedBadge.href = bridgeLink.href;
  integratedBadge.target = bridgeLink.target || '_blank';
  integratedBadge.rel = bridgeLink.rel || 'noopener';
  integratedBadge.setAttribute(
    'aria-label',
    productTitle
      ? `${productTitle}: sold out in NYC; available on the main site`
      : 'Sold out in NYC; available on the main site'
  );
  integratedBadge.innerHTML = `
    <span class="nfd-main-site-badge__eyebrow">NYC sold out</span>
    <span class="nfd-main-site-badge__cta">Available on Main Site <span aria-hidden="true">→</span></span>
  `;

  soldOutBadge.replaceWith(integratedBadge);
  bridge.dataset.nfdIntegrated = 'true';
  bridge.remove();
}

/**
 * Finds main-site availability links in newly rendered product cards.
 *
 * @param {ParentNode} [root]
 */
function integrateMainSiteAvailability(root = document) {
  if (root instanceof Element && root.matches(MAIN_SITE_BRIDGE_SELECTOR)) {
    integrateMainSiteBridge(root);
  }

  for (const bridge of root.querySelectorAll(MAIN_SITE_BRIDGE_SELECTOR)) {
    integrateMainSiteBridge(bridge);
  }
}

function initializeNfdCustomizations() {
  enhanceWorldwideLinks();
  integrateMainSiteAvailability();

  const headerGroup = document.querySelector('#header-group');
  if (headerGroup) {
    const headerObserver = new MutationObserver(() => enhanceWorldwideLinks(headerGroup));
    headerObserver.observe(headerGroup, { childList: true, subtree: true });
  }

  const productGridObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'attributes') {
        integrateMainSiteBridge(mutation.target);
        continue;
      }

      for (const addedNode of mutation.addedNodes) {
        if (addedNode instanceof Element) integrateMainSiteAvailability(addedNode);
      }
    }
  });
  productGridObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['style'],
    childList: true,
    subtree: true,
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeNfdCustomizations, { once: true });
} else {
  initializeNfdCustomizations();
}
